import type { RequestPdfParams } from '@/apis/pdf';

import { setCssPageMedia } from '@/utils/dom';

/** The PDF service's default viewport width; pre-`layoutWidth` reports were laid out at it. */
export const PDF_SERVICE_VIEWPORT_WIDTH = 1280;

interface PdfPageBox {
  width: number;
  height: number;
  margin?: number | { x: number; y: number };
}

/** `@page` box in CSS px. `layoutWidth` lays out at that width and scales to fit; else `scale` or 1. */
export type PdfPageLayout = PdfPageBox &
  ({ layoutWidth?: number; scale?: never } | { scale?: number; layoutWidth?: never });

function marginsOf({ margin = 0 }: PdfPageLayout) {
  return typeof margin === 'number' ? { x: margin, y: margin } : margin;
}

function printableWidth(layout: PdfPageLayout) {
  return layout.width - 2 * marginsOf(layout).x;
}

export function pdfScale(layout: PdfPageLayout): number {
  if (layout.layoutWidth) return printableWidth(layout) / layout.layoutWidth;
  return layout.scale ?? 1;
}

export function pdfLayoutWidth(layout: PdfPageLayout): number {
  return layout.layoutWidth ?? printableWidth(layout) / pdfScale(layout);
}

/** Paper for report routes with `meta.layout: 'horizontal'` (applied in App.vue). */
export const PDF_PAGE_HORIZONTAL: PdfPageLayout = {
  width: 1150,
  height: 735,
  layoutWidth: PDF_SERVICE_VIEWPORT_WIDTH,
};

/**
 * Installs the `@page` box and pins print `<body>` to the layout width. Chart.js canvases keep
 * the viewport-time size through page.pdf()'s reflow, so an unpinned body leaves charts off-centre.
 */
export function applyPdfPageLayout(layout: PdfPageLayout): void {
  const { x, y } = marginsOf(layout);
  setCssPageMedia(
    `size: ${layout.width}px ${layout.height}px; margin: ${y}px ${x}px;`,
    `@media print { body { width: ${pdfLayoutWidth(layout)}px; } }`,
  );
}

/** Paper and scale for the PDF request matching `applyPdfPageLayout(layout)`. */
export function pdfRequestPage(
  layout: PdfPageLayout,
): Required<Pick<RequestPdfParams, 'width' | 'height' | 'scale'>> {
  return { width: `${layout.width}px`, height: `${layout.height}px`, scale: pdfScale(layout) };
}
